import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../components/ui/AuthStateManager';
import Header from '../../components/ui/Header';
import ProgressIndicator from '../../components/ui/ProgressIndicator';
import PreviewHeader from './components/PreviewHeader';
import PlannerPreviewViewer from './components/PlannerPreviewViewer';
import PreviewSidebar from './components/PreviewSidebar';
import GenerationProgress from './components/GenerationProgress';

const PlannerPreview = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  
  const [currentPage, setCurrentPage] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showGenerationProgress, setShowGenerationProgress] = useState(false);
  const [plannerData, setPlannerData] = useState(null);

  // Load planner data from localStorage instead of mock data
  useEffect(() => {
    const savedData = localStorage.getItem('plannerWizardData');
    if (savedData) {
      try {
        const wizardData = JSON.parse(savedData);
        
        // Transform wizard data to planner data structure
        const transformedData = {
          id: searchParams?.get('id') || 'new-planner',
          coverTitle: wizardData?.personalization?.name || "My Life Planner",
          startDate: `${wizardData?.personalization?.year}-01-01`,
          endDate: `${wizardData?.personalization?.year}-12-31`,
          layout: wizardData?.layout || "Standard",
          colorScheme: wizardData?.theme || "Navy Blue",
          timeFormat: wizardData?.timeFormat || "12",
          firstDayOfWeek: "monday",
          includeWeekends: true,
          includeHolidays: true,
          sections: {
            monthly: true,
            weekly: true,
            daily: true,
            notes: wizardData?.sections?.includes('notes') || true,
            goals: wizardData?.sections?.includes('goals') || true,
            habits: wizardData?.sections?.includes('habits') || false
          },
          customization: {
            font: wizardData?.personalization?.font || "Inter",
            headerStyle: "Modern",
            accentColor: wizardData?.theme === 'classic' ? '#1E3A5F' : 
                          wizardData?.theme === 'warm' ? '#D97706' :
                          wizardData?.theme === 'nature' ? '#059669' :
                          wizardData?.theme === 'elegant' ? '#7C3AED' :
                          wizardData?.theme === 'coral' ? '#FF6B6B' : '#374151'
          },
          // Keep original wizard data for PDF generation
          originalWizardData: wizardData,
          createdAt: new Date()?.toISOString(),
          lastModified: new Date()?.toISOString(),
          status: "preview"
        };
        
        setPlannerData(transformedData);
      } catch (error) {
        console.error('Error loading wizard data:', error);
        // Fallback to default values if parsing fails
        setPlannerData({
          id: 'new-planner',
          coverTitle: "My Life Planner",
          status: "preview"
        });
      }
    } else {
      // No saved data, use minimal defaults
      setPlannerData({
        id: 'new-planner',
        coverTitle: "My Life Planner",
        status: "preview"
      });
    }
  }, [searchParams]);

  const handleUpdateSettings = (updatedSettings) => {
    setPlannerData(prev => ({
      ...prev,
      ...updatedSettings,
      lastModified: new Date()?.toISOString()
    }));
  };

  const handleGeneratePDF = async (finalSettings) => {
    setIsGenerating(true);
    setShowGenerationProgress(true);
    
    // Update planner data with final settings
    const finalPlannerData = {
      ...plannerData,
      ...finalSettings,
      status: 'generating'
    };
    setPlannerData(finalPlannerData);
  };

  const handleGenerationComplete = () => {
    setShowGenerationProgress(false);
    setIsGenerating(false);
    
    // Navigate to results page - localStorage will be read there
    navigate('/pdf-generation-results');
  };

  const handleCancelGeneration = () => {
    setShowGenerationProgress(false);
    setIsGenerating(false);
    setPlannerData(prev => ({
      ...prev,
      status: 'preview'
    }));
  };

  const handleEditSettings = () => {
    navigate('/planner-setup-wizard', {
      state: { plannerData }
    });
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    
    // Simulate save operation
    setTimeout(() => {
      setPlannerData(prev => ({
        ...prev,
        status: 'draft',
        lastModified: new Date()?.toISOString()
      }));
      setIsSaving(false);
    }, 1500);
  };

  const handleSharePreview = () => {
    // Simulate sharing functionality
    if (navigator.share) {
      navigator.share({
        title: plannerData?.coverTitle || 'My Planner',
        text: 'Check out my planner preview!',
        url: window.location?.href
      });
    } else {
      // Fallback - copy to clipboard
      navigator.clipboard?.writeText(window.location?.href);
      // In real app, show toast notification
    }
  };

  const progressSteps = [
    { id: 1, label: 'Setup', description: 'Configure your planner' },
    { id: 2, label: 'Preview', description: 'Review your design' },
    { id: 3, label: 'Generate', description: 'Download your planner' }
  ];

  if (!plannerData) {
    return (
      <div className="min-h-screen bg-background">
        <Header isAuthenticated={isAuthenticated} onLogout={() => navigate('/login')} />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="animate-pulse-gentle">
            <div className="w-8 h-8 bg-primary rounded-full"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header 
        isAuthenticated={isAuthenticated} 
        onLogout={() => navigate('/login')}
      />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Progress Indicator */}
          <div className="mb-8">
            <ProgressIndicator
              currentStep={2}
              totalSteps={3}
              steps={progressSteps}
              onStepClick={(step) => {
                if (step === 1) navigate('/planner-setup-wizard');
                if (step === 3 && !isGenerating) handleGeneratePDF(plannerData);
              }}
            />
          </div>

          {/* Preview Header */}
          <div className="mb-8">
            <PreviewHeader
              plannerData={plannerData}
              onSave={handleSaveDraft}
              onShare={handleSharePreview}
              isSaving={isSaving}
            />
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
            {/* Preview Viewer */}
            <div className="xl:col-span-3">
              <PlannerPreviewViewer
                plannerData={plannerData}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            </div>

            {/* Sidebar */}
            <div className="xl:col-span-1">
              <PreviewSidebar
                plannerData={plannerData}
                onUpdateSettings={handleUpdateSettings}
                onGeneratePDF={handleGeneratePDF}
                onEditSettings={handleEditSettings}
                isGenerating={isGenerating}
              />
            </div>
          </div>

          {/* Mobile Actions */}
          <div className="xl:hidden mt-8 grid grid-cols-2 gap-4">
            <button
              onClick={handleEditSettings}
              disabled={isGenerating}
              className="flex items-center justify-center space-x-2 px-4 py-3 bg-card border border-border rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
            >
              <span className="font-body font-medium">Edit Settings</span>
            </button>
            
            <button
              onClick={() => handleGeneratePDF(plannerData)}
              disabled={isGenerating}
              className="flex items-center justify-center space-x-2 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              <span className="font-body font-medium">
                {isGenerating ? 'Generating...' : 'Generate PDF'}
              </span>
            </button>
          </div>

          {/* Tips Section */}
          <div className="mt-12 bg-card border border-border rounded-lg p-6">
            <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
              Preview Tips
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-primary font-heading font-semibold text-sm">1</span>
                </div>
                <div>
                  <h4 className="font-body font-medium text-foreground mb-1">Navigate Pages</h4>
                  <p className="font-body text-sm text-muted-foreground">
                    Use arrow keys or click navigation buttons to browse through your planner pages.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-secondary font-heading font-semibold text-sm">2</span>
                </div>
                <div>
                  <h4 className="font-body font-medium text-foreground mb-1">Zoom & Inspect</h4>
                  <p className="font-body text-sm text-muted-foreground">
                    Use zoom controls to inspect details and ensure everything looks perfect.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-accent font-heading font-semibold text-sm">3</span>
                </div>
                <div>
                  <h4 className="font-body font-medium text-foreground mb-1">Final Adjustments</h4>
                  <p className="font-body text-sm text-muted-foreground">
                    Make last-minute changes in the sidebar before generating your final PDF.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Generation Progress Modal */}
      <GenerationProgress
        isVisible={showGenerationProgress}
        onComplete={handleGenerationComplete}
        onCancel={handleCancelGeneration}
        plannerData={plannerData}
      />
    </div>
  );
};

export default PlannerPreview;